/**
 * JSONLogic Adapter for Routing Rules
 *
 * Converts between react-querybuilder format and JSONLogic format.
 *
 * JSONLogic format:
 * { "<": [{ "var": "analysis.qaScore" }, 60] }
 * { "and": [{ "<": ... }, { ">": ... }] }
 *
 * QueryBuilder format:
 * { combinator: "and", rules: [{ field: "...", operator: "<", value: 60 }] }
 */

import type { RuleGroupType, RuleType } from 'react-querybuilder';

// JSONLogic types
type JsonLogicVar = { var: string };
type JsonLogicValue = string | number | boolean | JsonLogicVar;
type JsonLogicOperation = Record<string, JsonLogicValue[]>;
type JsonLogicRule =
  | JsonLogicOperation
  | { and: JsonLogicRule[] }
  | { or: JsonLogicRule[] };

/**
 * Convert react-querybuilder RuleGroup to JSONLogic
 */
export function queryBuilderToJsonLogic(
  ruleGroup: RuleGroupType
): Record<string, unknown> | null {
  if (!ruleGroup.rules || ruleGroup.rules.length === 0) {
    return null;
  }

  const jsonRules: JsonLogicRule[] = [];

  for (const rule of ruleGroup.rules) {
    if ('rules' in rule) {
      // Nested group
      const nested = queryBuilderToJsonLogic(rule as RuleGroupType);
      if (nested) {
        jsonRules.push(nested as JsonLogicRule);
      }
    } else {
      // Single rule
      const jsonRule = ruleToJsonLogic(rule as RuleType);
      if (jsonRule) {
        jsonRules.push(jsonRule);
      }
    }
  }

  if (jsonRules.length === 0) {
    return null;
  }

  if (jsonRules.length === 1) {
    // Single rule, no combinator needed
    return jsonRules[0] as Record<string, unknown>;
  }

  // Multiple rules with combinator
  return {
    [ruleGroup.combinator]: jsonRules,
  };
}

/**
 * Convert single rule to JSONLogic operation
 */
function ruleToJsonLogic(rule: RuleType): JsonLogicOperation | null {
  if (!rule.field || !rule.operator) {
    return null;
  }

  const fieldVar: JsonLogicVar = { var: rule.field };
  let value: JsonLogicValue = rule.value;

  // Handle boolean strings
  if (value === 'true') value = true;
  if (value === 'false') value = false;

  // Handle numeric strings (only non-empty strings)
  if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) {
    value = Number(value);
  }

  // Handle 'contains' operator - JSONLogic uses 'in' with reversed operands
  if (rule.operator === 'contains') {
    return {
      in: [value, fieldVar],
    } as JsonLogicOperation;
  }

  // Handle 'notContains' operator
  if (rule.operator === 'notContains') {
    return {
      '!': [{ in: [value, fieldVar] }],
    } as unknown as JsonLogicOperation;
  }

  // Handle 'beginsWith' operator
  if (rule.operator === 'beginsWith') {
    return {
      startsWith: [fieldVar, value],
    } as JsonLogicOperation;
  }

  // Handle 'endsWith' operator
  if (rule.operator === 'endsWith') {
    return {
      endsWith: [fieldVar, value],
    } as JsonLogicOperation;
  }

  return {
    [rule.operator]: [fieldVar, value],
  };
}

/**
 * Convert JSONLogic to react-querybuilder RuleGroup
 */
export function jsonLogicToQueryBuilder(
  jsonLogic: Record<string, unknown>
): RuleGroupType {
  const defaultGroup: RuleGroupType = {
    combinator: 'and',
    rules: [],
  };

  if (!jsonLogic || typeof jsonLogic !== 'object') {
    return defaultGroup;
  }

  // Check if it's a combinator (and/or)
  if ('and' in jsonLogic) {
    const andRules = jsonLogic.and;
    if (!Array.isArray(andRules)) {
      return defaultGroup;
    }
    return {
      combinator: 'and',
      rules: andRules
        .filter((r): r is Record<string, unknown> => r !== null && typeof r === 'object')
        .map((r) => parseJsonLogicRule(r)),
    };
  }

  if ('or' in jsonLogic) {
    const orRules = jsonLogic.or;
    if (!Array.isArray(orRules)) {
      return defaultGroup;
    }
    return {
      combinator: 'or',
      rules: orRules
        .filter((r): r is Record<string, unknown> => r !== null && typeof r === 'object')
        .map((r) => parseJsonLogicRule(r)),
    };
  }

  // Single rule
  const rule = parseJsonLogicRule(jsonLogic);
  if (rule) {
    return {
      combinator: 'and',
      rules: [rule],
    };
  }

  return defaultGroup;
}

/**
 * Parse single JSONLogic rule into QueryBuilder format
 */
function parseJsonLogicRule(jsonRule: Record<string, unknown>): RuleType | RuleGroupType {
  // Check for nested combinator
  if ('and' in jsonRule || 'or' in jsonRule) {
    return jsonLogicToQueryBuilder(jsonRule);
  }

  // Find the operator
  const operators = [
    '<',
    '<=',
    '>',
    '>=',
    '==',
    '!=',
    '===',
    '!==',
    'in',
    'startsWith',
    'endsWith',
  ];
  let foundOperator: string | null = null;
  let operands: unknown[] = [];

  for (const op of operators) {
    if (op in jsonRule) {
      foundOperator = op;
      operands = jsonRule[op] as unknown[];
      break;
    }
  }

  if (!foundOperator || !Array.isArray(operands) || operands.length < 2) {
    // Return empty rule
    return {
      field: '',
      operator: '==',
      value: '',
    };
  }

  // Handle 'in' operator specially (reversed operands for 'contains')
  if (foundOperator === 'in') {
    // 'in' format: { in: [value, { var: field }] }
    const secondOperand = operands[1];
    const field =
      secondOperand !== null &&
      typeof secondOperand === 'object' &&
      'var' in secondOperand &&
      typeof (secondOperand as Record<string, unknown>).var === 'string'
        ? ((secondOperand as Record<string, unknown>).var as string)
        : '';
    // Value can be string or number
    const containsValue =
      typeof operands[0] === 'string' || typeof operands[0] === 'number'
        ? operands[0]
        : String(operands[0] ?? '');
    return {
      field,
      operator: 'contains',
      value: containsValue,
    };
  }

  // Handle 'startsWith' operator
  if (foundOperator === 'startsWith') {
    const firstOperand = operands[0];
    const field =
      firstOperand !== null &&
      typeof firstOperand === 'object' &&
      'var' in firstOperand &&
      typeof (firstOperand as Record<string, unknown>).var === 'string'
        ? ((firstOperand as Record<string, unknown>).var as string)
        : '';
    return {
      field,
      operator: 'beginsWith',
      value: operands[1],
    };
  }

  // Handle 'endsWith' operator
  if (foundOperator === 'endsWith') {
    const firstOperand = operands[0];
    const field =
      firstOperand !== null &&
      typeof firstOperand === 'object' &&
      'var' in firstOperand &&
      typeof (firstOperand as Record<string, unknown>).var === 'string'
        ? ((firstOperand as Record<string, unknown>).var as string)
        : '';
    return {
      field,
      operator: 'endsWith',
      value: operands[1],
    };
  }

  // Extract field from { var: "field.path" }
  const firstOperand = operands[0];
  const field =
    firstOperand !== null &&
    typeof firstOperand === 'object' &&
    'var' in firstOperand &&
    typeof (firstOperand as Record<string, unknown>).var === 'string'
      ? ((firstOperand as Record<string, unknown>).var as string)
      : '';

  // Extract value
  let value = operands[1];

  // Convert boolean to string for select inputs
  if (typeof value === 'boolean') {
    value = String(value);
  }

  // Normalize operator (=== -> ==, !== -> !=)
  const normalizedOp =
    foundOperator === '===' ? '==' : foundOperator === '!==' ? '!=' : foundOperator;

  return {
    field,
    operator: normalizedOp,
    value,
  };
}

/**
 * Validate JSONLogic structure
 */
export function validateJsonLogic(jsonLogic: unknown): {
  valid: boolean;
  error?: string;
} {
  if (!jsonLogic || typeof jsonLogic !== 'object') {
    return { valid: false, error: 'invalid_jsonlogic_object' };
  }

  const obj = jsonLogic as Record<string, unknown>;

  // Check for valid operators or combinators
  const validKeys = [
    'and',
    'or',
    '<',
    '<=',
    '>',
    '>=',
    '==',
    '!=',
    '===',
    '!==',
    'in',
    'startsWith',
    'endsWith',
    '!',
    '+',
    '-',
    '*',
    '/',
  ];
  const keys = Object.keys(obj);

  if (keys.length === 0) {
    return { valid: false, error: 'empty_jsonlogic_object' };
  }

  for (const key of keys) {
    if (!validKeys.includes(key)) {
      return { valid: false, error: `Unknown operator: ${key}` };
    }
  }

  return { valid: true };
}

/**
 * Format JSONLogic for display
 */
export function formatJsonLogic(jsonLogic: Record<string, unknown>): string {
  return JSON.stringify(jsonLogic, null, 2);
}

/**
 * Parse JSONLogic from string
 */
export function parseJsonLogicString(
  str: string
): { success: true; data: Record<string, unknown> } | { success: false; error: string } {
  try {
    const parsed = JSON.parse(str);
    const validation = validateJsonLogic(parsed);
    if (!validation.valid) {
      return { success: false, error: validation.error || 'invalid_jsonlogic' };
    }
    return { success: true, data: parsed };
  } catch {
    return { success: false, error: 'invalid_json_syntax' };
  }
}

/**
 * Extract all field names (var references) from JSONLogic
 */
export function extractFieldsFromJsonLogic(jsonLogic: Record<string, unknown>): string[] {
  const fields: string[] = [];

  function traverse(obj: unknown): void {
    if (!obj || typeof obj !== 'object') return;

    if (Array.isArray(obj)) {
      for (const item of obj) {
        traverse(item);
      }
      return;
    }

    const record = obj as Record<string, unknown>;

    // Check if this is a var reference
    if ('var' in record && typeof record.var === 'string') {
      fields.push(record.var);
      return;
    }

    // Traverse all values
    for (const value of Object.values(record)) {
      traverse(value);
    }
  }

  traverse(jsonLogic);
  return [...new Set(fields)]; // Return unique fields
}

/**
 * Validate that all fields in JSONLogic exist in available fields list
 * Returns list of missing fields (empty if all valid)
 */
export function validateFieldsExist(
  jsonLogic: Record<string, unknown>,
  availableFields: Array<{ name: string }>
): string[] {
  const usedFields = extractFieldsFromJsonLogic(jsonLogic);
  const availableFieldNames = new Set(availableFields.map((f) => f.name));

  const missingFields: string[] = [];
  for (const field of usedFields) {
    // Skip base fields that are always available (record.*, analysis.*, metadata.*)
    if (
      field.startsWith('record.') ||
      field.startsWith('analysis.') ||
      field.startsWith('metadata.')
    ) {
      continue;
    }

    // Check if CRM field exists
    if (!availableFieldNames.has(field)) {
      missingFields.push(field);
    }
  }

  return missingFields;
}
